import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Plus, Edit2, Trash2, X, Check, Clock, Coins } from "lucide-react";
import { toast } from "sonner";

export default function AdminRewards() {
  const [tab, setTab] = useState("catalog");
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/rewards").then(({ data }) => setRewards(data));
    api.get("/admin/redemptions").then(({ data }) => setRedemptions(data));
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, []);

  const del = async (r) => {
    if (!window.confirm(`Xóa "${r.name}"?`)) return;
    await api.delete(`/admin/rewards/${r.id}`);
    load();
  };

  const confirm = async (r) => {
    await api.post(`/admin/redemptions/${r.id}/confirm`);
    toast.success("Đã xác nhận giao quà");
    load();
  };
  const cancel = async (r) => {
    if (!window.confirm("Hủy & hoàn coin?")) return;
    await api.post(`/admin/redemptions/${r.id}/cancel`);
    toast.success("Đã hủy và hoàn coin");
    load();
  };

  const pending = redemptions.filter((r) => r.status === "pending");

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-3"><Gift className="text-yellow-300" /> Đổi quà</h1>
          <p className="text-white/40 text-sm mt-1">{pending.length} yêu cầu đang chờ giao</p>
        </div>
        {tab === "catalog" && (
          <button onClick={() => setCreating(true)} data-testid="new-reward-btn" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center gap-2">
            <Plus size={15} /> Thêm quà
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-5">
        {["catalog", "redemptions"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`rw-admin-tab-${k}`}
            className={`px-4 py-2 rounded-md text-sm ${tab === k ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/40" : "bg-white/[0.03] text-white/60 border border-white/8"}`}>
            {k === "catalog" ? "Danh mục quà" : `Yêu cầu đổi (${pending.length})`}
          </button>
        ))}
      </div>

      {tab === "catalog" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rewards.map((r) => (
            <div key={r.id} className="rounded-xl bg-[#0c0d14] border border-white/8 p-4">
              <div className="text-3xl mb-2">{r.icon || "🎁"}</div>
              <div className="font-display font-semibold mb-1">{r.name}</div>
              <div className="text-xs text-white/50 mb-3">{r.description}</div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5 font-display font-bold text-yellow-300"><Coins size={12} /> {r.coin_price}</div>
                <div className="text-xs text-white/50">{r.stock === -1 ? "Không giới hạn" : `Còn ${r.stock}`}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(r)} className="flex-1 px-3 py-1.5 rounded text-xs border border-white/10 hover:bg-white/5 flex items-center justify-center gap-1"><Edit2 size={12} /> Sửa</button>
                <button onClick={() => del(r)} className="p-2 rounded text-red-400 hover:bg-red-500/10"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
          {redemptions.length === 0 ? (
            <div className="py-12 text-center text-white/40 text-sm">Chưa có yêu cầu đổi</div>
          ) : redemptions.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-4 border-b border-white/5" data-testid={`redemption-${r.id}`}>
              <div className="text-2xl">{r.reward_icon || "🎁"}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.reward_name}</div>
                <div className="text-xs text-white/40">{r.player_nickname} · {new Date(r.created_at).toLocaleString("vi-VN")}</div>
              </div>
              <div className="flex items-center gap-1.5 text-yellow-300 text-sm font-display font-bold"><Coins size={11} /> {r.coin_cost}</div>
              {r.status === "pending" ? (
                <div className="flex gap-1.5">
                  <button onClick={() => confirm(r)} data-testid={`confirm-${r.id}`} className="px-3 py-1.5 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 flex items-center gap-1"><Check size={11} /> Đã giao</button>
                  <button onClick={() => cancel(r)} className="px-3 py-1.5 rounded text-xs bg-red-500/10 text-red-300 border border-red-500/30 flex items-center gap-1"><X size={11} /> Hủy</button>
                </div>
              ) : (
                <span className={`text-xs ${r.status === "confirmed" ? "text-cyan-300" : "text-red-300"}`}>{r.status === "confirmed" ? "Đã giao" : "Hủy/hoàn"}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && <RewardModal reward={editing} onClose={() => { setCreating(false); setEditing(null); }} onDone={() => { setCreating(false); setEditing(null); load(); }} />}
    </div>
  );
}

const RewardModal = ({ reward, onClose, onDone }) => {
  const [f, setF] = useState({
    name: reward?.name || "",
    description: reward?.description || "",
    coin_price: reward?.coin_price || 50,
    stock: reward?.stock ?? -1,
    icon: reward?.icon || "🎁",
    active: reward?.active ?? true,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (reward) await api.patch(`/admin/rewards/${reward.id}`, f);
      else await api.post("/admin/rewards", { ...f, coin_price: Number(f.coin_price), stock: Number(f.stock) });
      toast.success(reward ? "Đã cập nhật" : "Đã thêm quà");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-display font-semibold">{reward ? "Sửa quà" : "Thêm quà"}</h3><button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button></div>
        <form onSubmit={submit} className="space-y-3" data-testid="reward-form">
          {[
            ["Tên *", "name", "text", true],
            ["Mô tả", "description", "text"],
            ["Icon (emoji)", "icon", "text"],
            ["Giá coin", "coin_price", "number"],
            ["Tồn kho (-1 = không giới hạn)", "stock", "number"],
          ].map(([l, k, type, req]) => (
            <label key={k} className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50">{l}</span>
              <input type={type} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
            </label>
          ))}
          <button type="submit" data-testid="submit-reward" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">{reward ? "Lưu" : "Tạo"}</button>
        </form>
      </div>
    </div>
  );
};
